/**
 * BulkPushDock — a small floating dock that surfaces active bulk push-
 * notification jobs on every page in the app (not just the Push
 * Notifications Compose panel).
 *
 * Visible only to admin / sub_admin / business_head roles, and only when
 * there is at least one in-flight bulk job. Polls
 * `GET /api/push/bulk-jobs` every 4s, hydrating from
 * `localStorage['push_active_bulk_jobs']` so navigation across pages /
 * browser tabs keeps the dock attached to live progress.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Loader2, CheckCircle2, XCircle, X, ChevronDown, ChevronUp, Send, Pause, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ADMIN_ROLES = ['admin', 'sub_admin', 'business_head'];
const LS_KEY = 'push_active_bulk_jobs';
const POLL_MS = 4000;

export default function BulkPushDock() {
  const { currentUser } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissedJobIds, setDismissedJobIds] = useState(() => new Set());
  const pollRef = useRef(null);

  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);
  const requesterId = currentUser?.id;

  const fetchJobs = useCallback(async () => {
    if (!requesterId) return;
    try {
      const res = await axios.get(`${API}/push/bulk-jobs`, {
        params: { requester_id: requesterId, include_finished: true },
      });
      let stored = [];
      try { stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch (_) { /* ignore */ }
      // Show:
      //   - any queued/running job (always)
      //   - any terminal job (completed/cancelled/failed) that finished within
      //     the last 30s OR doesn't yet have a finished_at stamp (transient
      //     window between status flip and persistence)
      // localStorage hints are used ONLY when the job is still active — once
      // a job hits a terminal state, drop it from LS regardless of recency so
      // cancelled jobs don't linger in the dock forever.
      const TERMINAL = new Set(['completed', 'cancelled', 'failed']);
      const TERMINAL_WINDOW_MS = 30_000;
      const now = Date.now();
      const visible = (res.data.jobs || []).filter(j => {
        if (dismissedJobIds.has(j.job_id)) return false;
        if (['queued', 'running', 'paused'].includes(j.status)) return true;
        if (TERMINAL.has(j.status)) {
          if (!j.finished_at) return true;  // edge case: terminal but no stamp
          const fin = new Date(j.finished_at).getTime();
          return !Number.isNaN(fin) && now - fin < TERMINAL_WINDOW_MS;
        }
        return false;
      });
      setJobs(visible);
      // Auto-clean LS:
      //   - drop ids that are no longer in `visible`
      //   - drop ids that are in a terminal state (regardless of recency)
      const liveIds = new Set(
        (res.data.jobs || []).filter(j => !TERMINAL.has(j.status)).map(j => j.job_id)
      );
      const trimmed = stored.filter(id => liveIds.has(id));
      if (trimmed.length !== stored.length) {
        try { localStorage.setItem(LS_KEY, JSON.stringify(trimmed)); } catch (_) { /* ignore */ }
      }
    } catch (e) {
      // Silent — dock is non-critical.
    }
  }, [requesterId, dismissedJobIds]);

  // Polling loop — only runs when admin user is authenticated AND the tab
  // is visible. Pausing on `visibilitychange` avoids background CPU/network
  // work on unfocused tabs.
  useEffect(() => {
    if (!isAdmin || !requesterId) return;
    fetchJobs();
    const start = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(fetchJobs, POLL_MS);
    };
    const stop = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    if (document.visibilityState === 'visible') start();
    const onVis = () => {
      if (document.visibilityState === 'visible') { fetchJobs(); start(); } else stop();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAdmin, requesterId, fetchJobs]);

  // Cancel a single job
  const cancelJob = async (jobId) => {
    try {
      await axios.post(`${API}/push/bulk-send/${jobId}/cancel`, null,
        { params: { requester_id: requesterId } });
      fetchJobs();
    } catch (_) { /* ignore */ }
  };

  const pauseJob = async (jobId) => {
    try {
      await axios.post(`${API}/push/bulk-send/${jobId}/pause`, null,
        { params: { requester_id: requesterId } });
      fetchJobs();
    } catch (_) { /* ignore */ }
  };

  const resumeJob = async (jobId) => {
    try {
      await axios.post(`${API}/push/bulk-send/${jobId}/resume`, null,
        { params: { requester_id: requesterId } });
      fetchJobs();
    } catch (_) { /* ignore */ }
  };

  const pauseAll = async () => {
    try {
      await axios.post(`${API}/push/bulk-jobs/pause-all`, null,
        { params: { requester_id: requesterId } });
      fetchJobs();
    } catch (_) { /* ignore */ }
  };

  const resumeAll = async () => {
    try {
      await axios.post(`${API}/push/bulk-jobs/resume-all`, null,
        { params: { requester_id: requesterId } });
      fetchJobs();
    } catch (_) { /* ignore */ }
  };

  const dismissJob = (jobId) => {
    setDismissedJobIds(prev => {
      const n = new Set(prev);
      n.add(jobId);
      return n;
    });
    setJobs(prev => prev.filter(j => j.job_id !== jobId));
  };

  // Hide the dock when on the Push Notifications page itself — the
  // Compose panel already shows full progress there, so the floating
  // dock would be redundant.
  const onPushPage = location.pathname === '/push-notifications'
    || (location.pathname === '/admin'
        && (new URLSearchParams(location.search).get('tab') === 'push'
            || new URLSearchParams(location.search).get('subTab') === 'push'));
  if (!isAdmin || !requesterId) return null;
  if (onPushPage) return null;
  if (jobs.length === 0) return null;

  const activeCount = jobs.filter(j => ['queued', 'running'].includes(j.status)).length;
  const pausedCount = jobs.filter(j => j.status === 'paused').length;
  const cancelledCount = jobs.filter(j => j.status === 'cancelled').length;
  const failedCount = jobs.filter(j => j.status === 'failed').length;
  const totalProcessed = jobs.reduce((acc, j) => acc + (j.processed || 0), 0);
  const totalEmails = jobs.reduce((acc, j) => acc + (j.total || 0), 0);
  let headerLabel;
  if (activeCount > 0) {
    headerLabel = `${activeCount} bulk push${activeCount > 1 ? 'es' : ''} running`;
  } else if (pausedCount > 0) {
    headerLabel = `${pausedCount} bulk push${pausedCount > 1 ? 'es' : ''} paused`;
  } else if (cancelledCount > 0 && cancelledCount === jobs.length) {
    headerLabel = `Bulk push${jobs.length > 1 ? 'es' : ''} cancelled`;
  } else if (failedCount > 0 && failedCount === jobs.length) {
    headerLabel = `Bulk push${jobs.length > 1 ? 'es' : ''} failed`;
  } else {
    headerLabel = `Bulk push${jobs.length > 1 ? 'es' : ''} complete`;
  }

  return (
    <div
      data-testid="bulk-push-dock"
      className="fixed bottom-6 right-6 z-[60] w-[340px] max-w-[calc(100vw-2rem)] bg-white border border-amber-300 rounded-xl shadow-2xl overflow-hidden"
      style={{ boxShadow: '0 10px 40px -10px rgba(217, 119, 6, 0.4)' }}
    >
      {/* Header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        data-testid="bulk-push-dock-toggle"
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {activeCount > 0
            ? <Loader2 className="w-4 h-4 animate-spin shrink-0" />
            : pausedCount > 0
              ? <Pause className="w-4 h-4 shrink-0" />
              : <CheckCircle2 className="w-4 h-4 shrink-0" />}
          <span className="font-semibold text-sm truncate">
            {headerLabel}
          </span>
          <span className="text-[11px] opacity-90 font-mono shrink-0">
            {totalProcessed}/{totalEmails}
          </span>
        </div>
        {collapsed ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-amber-100" data-testid="bulk-push-dock-list">
          {jobs.map(job => {
            const total = job.total || 0;
            const processed = job.processed || 0;
            const pct = total ? Math.round((processed / total) * 100) : 0;
            const done = ['completed', 'cancelled', 'failed'].includes(job.status);
            const ok = (job.results || []).filter(r => r.effective_success ?? r.success).length;
            const failed = total - ok;
            const etaSec = job.status === 'running' && total > processed
              ? Math.max(1, Math.ceil((total - processed) * 0.6))
              : 0;
            const etaText = etaSec >= 60 ? `~${Math.ceil(etaSec / 60)} min remaining` : `~${etaSec}s remaining`;
            return (
              <div key={job.job_id} className="p-3 space-y-2" data-testid={`bulk-push-dock-row-${job.job_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {job.status === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500 shrink-0" />}
                      {job.status === 'queued' && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500 shrink-0" />}
                      {job.status === 'paused' && <Pause className="w-3.5 h-3.5 text-blue-500 shrink-0" />}
                      {job.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                      {job.status === 'cancelled' && <XCircle className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                      {job.status === 'failed' && <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                      <span className="text-xs font-semibold text-slate-900 truncate" title={job.title || job.job_id}>
                        {job.status === 'running' && `Sending push ${processed}/${total}…`}
                        {job.status === 'queued' && `Queued ${total} recipient${total === 1 ? '' : 's'}`}
                        {job.status === 'paused' && `Paused at ${processed}/${total}`}
                        {job.status === 'completed' && `Completed — ${total} processed`}
                        {job.status === 'cancelled' && `Cancelled at ${processed}/${total}`}
                        {job.status === 'failed' && (job.error || 'Failed')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                      <span>Job <code className="font-mono text-[10px] bg-slate-100 px-1 rounded">{job.job_id.slice(0, 8)}</code></span>
                      {ok > 0 && <span className="text-emerald-600 font-medium">{ok} delivered</span>}
                      {failed > 0 && done && <span className="text-red-600 font-medium">{failed} failed</span>}
                      {(job.retried_count || 0) > 0 && (
                        <span className="text-purple-700 font-medium">↻{job.retried_count}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!done && job.status !== 'paused' && (
                      <button
                        onClick={() => pauseJob(job.job_id)}
                        className="text-[10px] text-blue-600 hover:text-blue-700 underline"
                        data-testid={`bulk-push-dock-pause-${job.job_id}`}
                      >
                        pause
                      </button>
                    )}
                    {job.status === 'paused' && (
                      <button
                        onClick={() => resumeJob(job.job_id)}
                        className="text-[10px] text-emerald-600 hover:text-emerald-700 underline"
                        data-testid={`bulk-push-dock-resume-${job.job_id}`}
                      >
                        resume
                      </button>
                    )}
                    {!done && (
                      <button
                        onClick={() => cancelJob(job.job_id)}
                        className="text-[10px] text-red-600 hover:text-red-700 underline"
                        data-testid={`bulk-push-dock-cancel-${job.job_id}`}
                      >
                        cancel
                      </button>
                    )}
                    {done && (
                      <button
                        onClick={() => dismissJob(job.job_id)}
                        className="text-slate-400 hover:text-slate-700"
                        data-testid={`bulk-push-dock-dismiss-${job.job_id}`}
                        aria-label="Dismiss"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <Progress value={pct} className="h-2" />
                <p className="text-[10px] text-slate-400">
                  {pct}% complete
                  {job.status === 'running' && total > processed && ` · ${etaText}`}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk pause/resume controls — only when several jobs are in flight */}
      {!collapsed && (activeCount + pausedCount) >= 2 && (
        <div className="px-3 py-2 bg-white border-t border-amber-100 flex items-center justify-end gap-2" data-testid="bulk-push-dock-bulk-controls">
          {activeCount > 0 && (
            <button
              onClick={pauseAll}
              data-testid="bulk-push-dock-pause-all"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50 transition-colors"
            >
              <Pause className="w-3 h-3" /> Pause all ({activeCount})
            </button>
          )}
          {pausedCount > 0 && (
            <button
              onClick={resumeAll}
              data-testid="bulk-push-dock-resume-all"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 border border-emerald-200 rounded-md px-2 py-1 hover:bg-emerald-50 transition-colors"
            >
              <Play className="w-3 h-3" /> Resume all ({pausedCount})
            </button>
          )}
        </div>
      )}

      {/* Footer */}
      {!collapsed && (
        <div className="px-3 py-2 bg-amber-50 border-t border-amber-100 flex items-center justify-between">
          <span className="text-[10px] text-slate-500">
            Auto-refreshing every {POLL_MS / 1000}s
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[10px] text-amber-700 hover:text-amber-900 hover:bg-amber-100"
            onClick={() => navigate('/push-notifications')}
            data-testid="bulk-push-dock-open-page"
          >
            <Send className="w-3 h-3 mr-1" />
            Open
          </Button>
        </div>
      )}
    </div>
  );
}
